import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Organisation, PricingPlan } from '../types';
import { PlatformHealthSection } from '../components/PlatformHealthSection';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState<{
    status: string;
    timestamp: string;
    db?: { pathEnv?: string; pathResolved?: string };
    counts?: { plans: number; features: number; tiers: number };
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansData, orgsData, health] = await Promise.all([
          api.getAllPlans(),
          api.getOrganisations(),
          api.healthCheck(),
        ]);

        setPlans(Array.isArray(plansData) ? plansData : []);
        setOrganisations(Array.isArray(orgsData) ? orgsData : []);
        setHealthStatus(health);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        try {
          const plansData = await api.getPlans();
          setPlans(plansData);
        } catch (fallbackError) {
          console.error('Error fetching fallback plans data:', fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const globalPlans = plans.filter((p) => p.isGlobal === true);
  const customPlans = plans.filter((p) => p.isGlobal === false);

  const handlePlanCardClick = () => navigate('/catalog/plans');

  const handlePlanClick = (_planId: string) => {
    navigate('/catalog/plans');
  };

  const handleOrganisationClick = (orgId: string) => {
    navigate(`/orgs/${orgId}`);
  };

  // Plan-related stats only — cross-org metrics (Organisations / Projects /
  // Users / Programs online) live in the PlatformHealthSection below, sourced
  // from /api/platform/overview rather than the local portal DB.
  const stats = [
    {
      name: 'Total Plans',
      value: plans.length,
      color: 'bg-blue-500',
      subtitle: `${customPlans.length} custom, ${globalPlans.length} global`,
    },
    {
      name: 'Active Plans',
      value: plans.filter((p) => p.active !== false).length,
      color: 'bg-yellow-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content mb-2">Repro Billing Dashboard</h1>
        <p className="text-base-content/70">
          Manage pricing plans, organisations, and monitor usage
        </p>

        {healthStatus && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <span className="text-sm text-green-700">
                  API Status: {healthStatus.status} –{' '}
                  {new Date(healthStatus.timestamp).toLocaleString()}
                </span>
              </div>
              {healthStatus?.counts && (
                <div className="text-xs text-green-700">
                  plans: {healthStatus.counts.plans} | features: {healthStatus.counts.features} |
                  tiers: {healthStatus.counts.tiers}
                </div>
              )}
            </div>
            {healthStatus?.db && (
              <div className="mt-2 text-xs text-green-700">
                DB: {healthStatus.db.pathResolved || healthStatus.db.pathEnv || 'auto'}
              </div>
            )}
          </div>
        )}
      </div>

      <PlatformHealthSection />

      {/* Plan stats — portal-specific. Cross-org counts are in Platform Health above. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="usage-metric cursor-pointer hover:shadow-lg transition-shadow duration-200"
            onClick={handlePlanCardClick}
            title={`Click to go to ${stat.name.toLowerCase()} page`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="metric-label">{stat.name}</p>
                {stat.subtitle && (
                  <p className="text-xs text-base-content/70 mt-1">{stat.subtitle}</p>
                )}
              </div>
              <div
                className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}
              >
                <span className="text-white font-bold text-lg">{stat.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pricing Plans Overview */}
        <div className="bg-base-100 shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-base-content mb-4">Pricing Plans</h2>
          <div className="space-y-3">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="flex items-center justify-between p-3 bg-base-200 rounded-md cursor-pointer hover:bg-base-200 transition-colors duration-200"
                onClick={() => handlePlanClick(plan.id)}
                title={`Click to view plan: ${plan.displayName || plan.name}`}
              >
                <div>
                  <h3 className="font-medium text-base-content">{plan.displayName}</h3>
                  <p className="text-sm text-base-content/70">{plan.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-base-content">
                    {(typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0) > 0
                      ? `£${typeof plan.price === 'number' ? plan.price : plan.price?.amount || 0}`
                      : 'Free'}
                  </p>
                  <p className="text-sm text-base-content/70">
                    {typeof plan.price === 'object' ? plan.price?.period : 'month'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Organisations Overview */}
        <div className="bg-base-100 shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-base-content mb-4">Recent Organisations</h2>
          <div className="space-y-3">
            {organisations.slice(0, 5).map((org) => {
              const plan = plans.find((p) => p.id === org.planId);
              return (
                <div
                  key={org.id}
                  className="flex items-center justify-between p-3 bg-base-200 rounded-md cursor-pointer hover:bg-base-200 transition-colors duration-200"
                  onClick={() => handleOrganisationClick(org.id)}
                  title={`Click to view organisation: ${org.name}`}
                >
                  <div>
                    <h3 className="font-medium text-base-content">{org.name}</h3>
                    <p className="text-sm text-base-content/70">{org.billingEmail}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-base-content">
                      {plan?.displayName || 'Unknown Plan'}
                    </p>
                    <p className="text-sm text-base-content/70">
                      {org.settings?.maxProjects || org.maxProjects || 'N/A'} projects
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
